import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as templatesApi from '../api/templates';

export function AdminTemplates() {
  const { selectedOrg } = useOrg();
  const { isAdmin, canReadAssets } = usePermissions();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    reportTypeId: '',
    format: 'csv',
    schemaMap: { columns: [] }
  });
  const [newColumn, setNewColumn] = useState({ name: '', sourceField: '', type: 'string' });

  const loadTemplates = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await templatesApi.getTemplates(selectedOrg.id);
      setTemplates(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleOpenCreate = () => {
    setSelectedTemplate(null);
    setForm({
      name: '',
      description: '',
      reportTypeId: 'report-type-utility-usage',
      format: 'csv',
      schemaMap: { columns: [] }
    });
    setShowModal(true);
  };

  const handleOpenEdit = (template) => {
    setSelectedTemplate(template);
    setForm({
      name: template.name,
      description: template.description || '',
      reportTypeId: template.reportTypeId,
      format: template.format,
      schemaMap: { ...template.schemaMap }
    });
    setShowModal(true);
  };

  const handleViewVersions = async (template) => {
    if (!selectedOrg) return;
    try {
      const vers = await templatesApi.getTemplateVersions(selectedOrg.id, template.id);
      setVersions(vers);
      setSelectedTemplate(template);
      setShowVersionsModal(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSave = async () => {
    if (!selectedOrg || saving) return;
    setSaving(true);
    try {
      if (selectedTemplate) {
        await templatesApi.updateTemplate(selectedOrg.id, selectedTemplate.id, {
          name: form.name,
          description: form.description,
          schemaMap: form.schemaMap
        });
      } else {
        await templatesApi.createTemplate(selectedOrg.id, form);
      }
      setShowModal(false);
      loadTemplates();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddColumn = () => {
    if (!newColumn.name || !newColumn.sourceField) return;
    setForm(prev => ({
      ...prev,
      schemaMap: {
        ...prev.schemaMap,
        columns: [...prev.schemaMap.columns, { ...newColumn }]
      }
    }));
    setNewColumn({ name: '', sourceField: '', type: 'string' });
  };

  const handleRemoveColumn = (index) => {
    setForm(prev => ({
      ...prev,
      schemaMap: {
        ...prev.schemaMap,
        columns: prev.schemaMap.columns.filter((_, i) => i !== index)
      }
    }));
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view templates.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Report Templates"
        subtitle="Manage report output templates and schema mappings"
      />

      <div style={styles.toolbar}>
        {isAdmin && (
          <button
            onClick={handleOpenCreate}
            style={styles.createButton}
            data-testid="create-template-button"
          >
            + New Template
          </button>
        )}
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading templates...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="templates-table">
            <thead>
              <tr>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Report Type</th>
                <th style={styles.th}>Format</th>
                <th style={styles.th}>Columns</th>
                <th style={styles.th}>Version</th>
                <th style={styles.th}>Updated</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.map(template => (
                <tr key={template.id} style={styles.tr} data-testid={`template-${template.id}`}>
                  <td style={styles.td}>
                    <div style={styles.templateName}>{template.name}</div>
                    <div style={styles.templateDesc}>{template.description}</div>
                  </td>
                  <td style={styles.td}>{template.reportTypeId}</td>
                  <td style={styles.td}>
                    <span style={styles.formatBadge}>{template.format.toUpperCase()}</span>
                  </td>
                  <td style={styles.td}>{template.schemaMap.columns.length}</td>
                  <td style={styles.td}>v{template.currentVersion}</td>
                  <td style={styles.td}>{new Date(template.updatedAt).toLocaleDateString()}</td>
                  <td style={styles.td}>
                    <button
                      onClick={() => handleViewVersions(template)}
                      style={styles.actionButton}
                      data-testid={`versions-${template.id}`}
                    >
                      Versions
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleOpenEdit(template)}
                        style={styles.editButton}
                        data-testid={`edit-${template.id}`}
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {templates.length === 0 && (
                <tr>
                  <td colSpan="7" style={styles.emptyState}>No templates found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="template-modal">
            <h3 style={styles.modalTitle}>
              {selectedTemplate ? 'Edit Template' : 'New Template'}
            </h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                style={styles.input}
                data-testid="template-name-input"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Description</label>
              <input
                type="text"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                style={styles.input}
                data-testid="template-description-input"
              />
            </div>
            {!selectedTemplate && (
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Report Type</label>
                  <select
                    value={form.reportTypeId}
                    onChange={(e) => setForm(prev => ({ ...prev, reportTypeId: e.target.value }))}
                    style={styles.select}
                    data-testid="template-report-type-input"
                  >
                    <option value="report-type-utility-usage">Utility Usage</option>
                    <option value="report-type-usage-summary">Usage Summary</option>
                    <option value="report-type-uptime">Uptime</option>
                    <option value="report-type-revenue">Revenue</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Format</label>
                  <select
                    value={form.format}
                    onChange={(e) => setForm(prev => ({ ...prev, format: e.target.value }))}
                    style={styles.select}
                    data-testid="template-format-input"
                  >
                    <option value="csv">CSV</option>
                    <option value="xlsx">Excel</option>
                  </select>
                </div>
              </div>
            )}
            <h4 style={styles.sectionTitle}>Schema Map (Columns)</h4>
            <div style={styles.columnsList}>
              {form.schemaMap.columns.map((col, index) => (
                <div key={index} style={styles.columnItem}>
                  <span style={styles.columnName}>{col.name}</span>
                  <span style={styles.columnSource}>← {col.sourceField}</span>
                  <span style={styles.columnType}>{col.type}</span>
                  <button
                    onClick={() => handleRemoveColumn(index)}
                    style={styles.removeButton}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <div style={styles.addColumnRow}>
              <input
                type="text"
                placeholder="Column name"
                value={newColumn.name}
                onChange={(e) => setNewColumn(prev => ({ ...prev, name: e.target.value }))}
                style={styles.smallInput}
                data-testid="column-name-input"
              />
              <input
                type="text"
                placeholder="Source field"
                value={newColumn.sourceField}
                onChange={(e) => setNewColumn(prev => ({ ...prev, sourceField: e.target.value }))}
                style={styles.smallInput}
                data-testid="column-source-input"
              />
              <select
                value={newColumn.type}
                onChange={(e) => setNewColumn(prev => ({ ...prev, type: e.target.value }))}
                style={styles.smallSelect}
                data-testid="column-type-input"
              >
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="date">Date</option>
              </select>
              <button
                onClick={handleAddColumn}
                style={styles.addButton}
                data-testid="add-column-button"
              >
                Add
              </button>
            </div>
            <div style={styles.modalActions}>
              <button onClick={() => setShowModal(false)} style={styles.cancelButton}>
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name}
                style={styles.submitButton}
                data-testid="save-template-button"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showVersionsModal && selectedTemplate && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="versions-modal">
            <h3 style={styles.modalTitle}>Version History: {selectedTemplate.name}</h3>
            <table style={styles.versionsTable}>
              <thead>
                <tr>
                  <th style={styles.th}>Version</th>
                  <th style={styles.th}>Columns</th>
                  <th style={styles.th}>Created</th>
                </tr>
              </thead>
              <tbody>
                {versions.map(v => (
                  <tr key={v.id} style={styles.tr}>
                    <td style={styles.td}>v{v.version}</td>
                    <td style={styles.td}>{v.schemaMap.columns?.length || 0}</td>
                    <td style={styles.td}>{new Date(v.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={styles.modalActions}>
              <button onClick={() => setShowVersionsModal(false)} style={styles.cancelButton}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  toolbar: { display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' },
  createButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.875rem', color: '#4a5568' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '1rem', fontSize: '0.875rem' },
  templateName: { fontWeight: '500' },
  templateDesc: { fontSize: '0.75rem', color: '#718096' },
  formatBadge: { padding: '0.25rem 0.5rem', background: '#e2e8f0', borderRadius: '4px', fontSize: '0.75rem' },
  actionButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #718096', background: 'white', color: '#718096', fontSize: '0.75rem', cursor: 'pointer', marginRight: '0.5rem' },
  editButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #3182ce', background: 'white', color: '#3182ce', fontSize: '0.75rem', cursor: 'pointer' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: '8px', padding: '1.5rem', minWidth: '600px', maxWidth: '700px', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
  modalTitle: { margin: '0 0 1.5rem 0', fontSize: '1.25rem', fontWeight: '600' },
  sectionTitle: { margin: '1.5rem 0 0.75rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#4a5568' },
  formGroup: { marginBottom: '1rem', flex: 1 },
  formRow: { display: 'flex', gap: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#4a5568' },
  input: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem' },
  select: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white' },
  columnsList: { marginBottom: '1rem' },
  columnItem: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem', background: '#f7fafc', borderRadius: '4px', marginBottom: '0.25rem' },
  columnName: { fontWeight: '500', flex: 1 },
  columnSource: { color: '#718096', fontSize: '0.75rem' },
  columnType: { padding: '0.125rem 0.375rem', background: '#e2e8f0', borderRadius: '4px', fontSize: '0.625rem' },
  removeButton: { padding: '0.125rem 0.375rem', border: 'none', background: '#fed7d7', color: '#c53030', borderRadius: '4px', cursor: 'pointer' },
  addColumnRow: { display: 'flex', gap: '0.5rem', alignItems: 'center' },
  smallInput: { flex: 1, padding: '0.375rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.75rem' },
  smallSelect: { padding: '0.375rem', borderRadius: '4px', border: '1px solid #e2e8f0', fontSize: '0.75rem', background: 'white' },
  addButton: { padding: '0.375rem 0.75rem', borderRadius: '4px', border: '1px solid #48bb78', background: '#48bb78', color: 'white', fontSize: '0.75rem', cursor: 'pointer' },
  versionsTable: { width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' },
  modalActions: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' },
  cancelButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  submitButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer' }
};
